<template>
  <div class="onboarding-flower-bed-weeding-form">
    <LsSelect
      v-model="flower_bed_count"
      :options="flowerBedCountOptions"
      label="How many flower beds would you like to get cleaned up?"
      track-id="onboarding_flower_bed_weeding_form-flower_bed_count"
      name="flower_bed_count"
      test-id="flower_bed_count"
      :validation="{ required: true }"
      @input="updateFlowerBedCount"
    />
    <div v-if="flower_beds.length">
      <LsInputLabel label="What is the size and location of the flower bed(s)?" />
      <div
        v-for="(bed, idx) in flower_beds"
        :key="idx"
        class="flower-bed-weeding-form--size-location-container"
        test-id="flower_bed"
      >
        <span class="flower-bed-weeding-form-size-location-count"> #{{ idx + 1 }} </span>
        <LsSelect
          v-model="bed.size"
          :options="flowerBedSizeOptions"
          track-id="onboarding_flower_bed_weeding_form-flower_bed_size"
          :name="`bed_size-${idx}`"
          test-id="bed_size"
          placeholder="- Size -"
          :validation="{ required: true }"
          class="flower-bed-weeding-form--size"
          @input="openBedSizeCustomModal(bed, idx)"
        />
        <LsSelect
          v-model="bed.location"
          :options="flowerBedLocationOptions"
          track-id="onboarding_flower_bed_weeding_form-flower_bed_location"
          :name="`bed_location-${idx}`"
          test-id="bed_location"
          placeholder="- Location -"
          :validation="{ required: true }"
          class="flower-bed-weeding-form--location"
        />
      </div>
    </div>
    <LsRadios
      v-model="desired_handling"
      :validation="{ required: true }"
      label="Would you like your crew to cut the weeds with weed eater and then apply spray or gel?"
      :options="QuoteFlowerBedWeedingOptions.desiredHandling"
      name="desired_handling"
      track-id="onboarding_flower_bed_weeding_form-desired_handling"
    />
    <LsRadios
      v-model="desired_haulaway"
      :validation="{ required: true }"
      label="Would you like your crew to haul and dispose of the weeds?"
      :options="QuoteFlowerBedWeedingOptions.desiredHaulaway"
      name="desired_haulaway"
      track-id="onboarding_flower_bed_weeding_form-desired_haulaway"
    />
    <LsInput
      v-model="details"
      track-id="onboarding_flower_bed_weeding_form-details"
      test-id="details"
      label="Any other details you would like your lawn care pro to know?"
      name="details"
      type="textarea"
    />
    <LsInputMasked
      v-model="budget"
      track-id="onboarding_flower_bed_weeding_form-budget"
      test-id="budget"
      label="What is your budget for this work?"
      name="budget"
      mask="currency"
    />
    <LsUploadPhotos
      v-model="images"
      track-id="onboarding_flower_bed_weeding_form-images"
      test-id="images"
      label="Upload photos"
      name="images"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import {
  LsInput,
  LsInputMasked,
  LsInputLabel,
  LsSelect,
  LsUploadPhotos,
  LsRadios,
} from '@/components/common';
import { QuoteFlowerBedWeedingOptions } from 'ls-constants';
import { mapActions, mapGetters } from 'vuex';
import { ONBOARDING__UPDATE_QUOTE, ONBOARDING__SELECTED_SERVICE } from '@/store/types';
import { lsRouterHelpers, lsValidationService, lsModalService } from 'ls-vue-common';
import { BedSizeCustomModal } from '@/modals';
import { lsUploadPhotosHelper } from '@/helpers';

export default {
  name: 'OnboardingFlowerBedWeedingForm',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsInput,
    LsInputMasked,
    LsSelect,
    LsUploadPhotos,
    LsInputLabel,
    LsRadios,
  },
  data() {
    return {
      service_id: lsRouterHelpers.getParam(this.$route, 'service_id'),

      flower_bed_count: null,
      customBedSizes: [],

      // form
      budget: '',
      images: [],

      // additional
      details: '',
      desired_haulaway: 'haulaway',
      desired_handling: 'organic_materials',
      flower_beds: [],
    };
  },
  computed: {
    ...mapGetters([ONBOARDING__SELECTED_SERVICE]),
    QuoteFlowerBedWeedingOptions() {
      return _.cloneDeep(QuoteFlowerBedWeedingOptions);
    },
    selectedService() {
      return this[ONBOARDING__SELECTED_SERVICE](this.service_id);
    },
    flowerBedCountOptions() {
      return _.times(20, (idx) => {
        return { value: idx + 1, label: _.toString(idx + 1), key: idx + 1 };
      });
    },
    flowerBedSizeOptions() {
      const sizes = _.cloneDeep(QuoteFlowerBedWeedingOptions.sizes);
      const sizesAndCustomBeds = _.concat(this.customBedSizes, sizes);
      return _.map(sizesAndCustomBeds, (size) => {
        return { value: size.value, label: size.label, key: size.value };
      });
    },
    flowerBedLocationOptions() {
      const locations = _.cloneDeep(QuoteFlowerBedWeedingOptions.locations);
      return _.map(locations, (location) => {
        return {
          value: location.value,
          label: location.label,
          key: location.value,
        };
      });
    },
  },
  created() {
    const { additional, budget } = this.selectedService;

    this.flower_bed_count = additional.flower_bed_count || null;
    this.flower_beds = additional.flower_beds;

    _.forEach(this.flower_beds, ({ size }) => {
      if (!_.some(QuoteFlowerBedWeedingOptions.sizes, { value: size })) {
        this.customBedSizes.push({
          key: size,
          value: size,
          label: _.replace(size, 'x', ' x '),
        });
      }
    });

    this.desired_haulaway = additional.desired_haulaway || this.desired_haulaway;
    this.desired_handling = additional.desired_handling || this.desired_handling;

    this.details = _.isString(additional.details) ? additional.details : '';
    this.budget = budget ? _.toString(budget) : '';
  },
  methods: {
    ...mapActions([ONBOARDING__UPDATE_QUOTE]),
    async updateQuote() {
      const isFormValid = await this.$validator.validateAll();
      const isUploadValid = lsUploadPhotosHelper.isValidUpload(this.images);

      if (!isFormValid || !isUploadValid) {
        return lsValidationService.scrollToInvalid();
      }

      this[ONBOARDING__UPDATE_QUOTE]({
        selectedService: this.selectedService,
        formData: {
          additional: {
            desired_haulaway: this.desired_haulaway,
            desired_handling: this.desired_handling,
            flower_beds: this.flower_beds,
            details: this.details,
          },
          budget: this.budget,
        },
        images: this.images,
      });
    },
    openBedSizeCustomModal(bed, idx) {
      // TODO: next person in should refactor this to breakout setCustomBedSize & close into seperate functions
      // so they can be tested.  See InstantQuoteFlowerBedWeedingForm & associated tests for the pattern
      if (bed.size === 'custom') {
        lsModalService.open({
          parent: this,
          component: BedSizeCustomModal,
          props: {
            setCustomBedSize: ({ width, length }) => {
              this.flower_beds[idx].size = `${length}ftx${width}ft`;
              this.customBedSizes = [
                ...this.customBedSizes,
                {
                  value: `${length}ftx${width}ft`,
                  label: `${length}ft x ${width}ft`,
                  key: `${length}ftx${width}ft`,
                },
              ];
            },
          },
          events: {
            close: () => {
              if (_.get(this.flower_beds[idx], 'size') === 'custom') {
                this.flower_beds[idx] = { ...this.flower_beds[idx], size: null };
                this.updateFlowerBedCount(this.flower_bed_count);
              }
            },
          },
        });
      }
    },
    updateFlowerBedCount(count) {
      this.flower_beds = _.times(count, (idx) => {
        return _.get(this.flower_beds, idx, { size: null, location: null });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.onboarding-flower-bed-weeding-form > :not(:last-child) {
  margin-bottom: 50px;
}

.flower-bed-weeding-form--size-location-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 10px;
}

.flower-bed-weeding-form--size {
  flex: 1;
  padding: 0px 10px;
}

.flower-bed-weeding-form--location {
  flex: 1;
}

.flower-bed-weeding-form-size-location-count {
  font-weight: bold;
  width: 5px;
  padding-right: 20px;
  padding-top: 5px;
}
</style>
